import { Component, useEffect, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router";
import { accountArea, areas, placeOf } from "../shell/navigation";
import { useTexts } from "../texts";
import { PageHead } from "../ui/kit";
import { useMissingOrg } from "../shell/openOrg";
import { PageState } from "../ui/pieces";

// Estados de página del Admin (C40, especificación §5.12): siempre dentro del marco y con su encabezado, para que se sepa
// qué pasó y por dónde seguir. Un id que no existe o está fuera de la cartera muestra «No encontramos esta empresa»
// (antes el React volvía a la lista sin decir nada).

/** `no-encontrado` · Empresas › ← Empresa. */
export function OrgNotFound() {
  const { L } = useTexts();
  useTitle(L("ADM_NOT_FOUND_ORG"));
  useMissingOrg();
  return (
    <PageState
      head={<PageHead title={L("ADM_COMPANY")} back={{ href: "/Organizations/Index", label: L("ADM_AREA_ORGS") }} />}
      title={L("ADM_NOT_FOUND_ORG")}
      line={L("ADM_NOT_FOUND_ORG_LINE")}
      action={
        <a className="tm-btn secondary" href="/Organizations/Index">
          {L("ADM_GO_ORGS")}
        </a>
      }
    />
  );
}

/**
 * Título de la pantalla donde se está (la sección o el área de la dirección): el encabezado de un estado de página lo
 * conserva y el bloque dice el motivo una sola vez (C40 1.14.8, AR3).
 */
export function useScreenTitle(fallback?: string): string {
  const { L } = useTexts();
  const location = useLocation();
  const place = placeOf(location.pathname, location.search);
  const section = place.sections.find((item) => item.key === place.section);
  const area = [...areas, accountArea].find((item) => item.key === place.area);
  if (place.area === "orgs" && place.orgId) return L("ADM_COMPANY");
  return section ? L(section.labelKey) : area ? L(area.labelKey) : (fallback ?? `Bheli ${L("ADM_PRODUCT")}`);
}

/** «Inicio»: el título del error cuando no se sabe en qué pantalla falló (RC1 · K5, AF10, C40 1.14.8). */
const homeTitle = (L: (key: string) => string) =>
  L(areas.find((item) => item.key === "home")?.labelKey ?? "ADM_AREA_HOME");

/** Una dirección que no existe. */
export function PageNotFound() {
  const { L } = useTexts();
  const title = useScreenTitle();
  useTitle(L("ADM_NOT_FOUND_PAGE"));
  return (
    <PageState
      head={<PageHead title={title} />}
      title={L("ADM_NOT_FOUND_PAGE")}
      line={L("ADM_NOT_FOUND_PAGE_LINE")}
      action={
        <a className="tm-btn secondary" href="/">
          {L("ADM_GO_HOME")}
        </a>
      }
    />
  );
}

/** Sin permiso para ver la pantalla (el encabezado dice la pantalla; el bloque, el motivo). */
export function Forbidden() {
  const { L } = useTexts();
  const title = useScreenTitle();
  useTitle(L("ADM_FORBIDDEN"));
  return (
    <PageState
      head={<PageHead title={title} />}
      title={L("ADM_FORBIDDEN")}
      line={L("ADM_FORBIDDEN_LINE")}
      action={
        <a className="tm-btn secondary" href="/">
          {L("ADM_GO_HOME")}
        </a>
      }
    />
  );
}

/**
 * Error inesperado: qué pasó, la referencia para dictar a soporte (C52) y «Reintentar». RC1 · K5 (AF10, C40): el título
 * es el de la pantalla donde falló; si no se sabe (la dirección /Error del Blazor, o falló el marco entero), «Inicio»,
 * nunca el nombre del producto.
 */
export function UnexpectedError({ reference }: { reference?: string | null }) {
  const { L } = useTexts();
  const title = useScreenTitle(homeTitle(L));
  useTitle(L("ADM_ERROR_TITLE"));
  return (
    <PageState
      head={<PageHead title={title} />}
      title={L("ADM_ERROR_TITLE")}
      line={reference ? L("ADM_ERROR_LINE") : L("ADM_ERROR_LINE_NOREF")}
      reference={reference}
      action={
        <>
          <button type="button" className="tm-btn secondary" onClick={() => window.location.reload()}>
            {L("ADM_RETRY")}
          </button>
          <a className="tm-btn secondary" href="/">
            {L("ADM_GO_HOME")}
          </a>
        </>
      }
    />
  );
}

/** `/Error?ref=` (la dirección del Blazor): el error con la referencia que traiga. */
export function ErrorRoute() {
  const reference = new URLSearchParams(window.location.search).get("ref");
  return <UnexpectedError reference={reference && /^[A-Za-z0-9-]{4,64}$/.test(reference) ? reference : null} />;
}

function useTitle(title: string) {
  useEffect(() => {
    document.title = `${title} - Bheli Admin`;
  }, [title]);
}

/** Referencia corta para dictar a soporte; viaja también en el reporte al Error Hub. */
const newReference = () => `INC-${Math.random().toString(16).slice(2, 8).toUpperCase()}`;

/**
 * Si una pantalla falla al dibujarse, el marco sigue y la pantalla muestra C40 «Algo salió mal» con su referencia. El
 * error va al Error Hub por el puente del navegador (fail-open: si no llega, la pantalla igual se muestra).
 */
export class ScreenBoundary extends Component<{ children: ReactNode; resetKey: string }, { reference: string | null }> {
  override state = { reference: null as string | null };

  static getDerivedStateFromError() {
    return { reference: newReference() };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    const reference = this.state.reference ?? newReference();
    void reportClientError(error, info, reference);
  }

  override componentDidUpdate(previous: { resetKey: string }) {
    if (previous.resetKey !== this.props.resetKey && this.state.reference) this.setState({ reference: null });
  }

  override render() {
    return this.state.reference ? <UnexpectedError reference={this.state.reference} /> : this.props.children;
  }
}

/**
 * RC1 · K5 (AF10): si falla el marco entero (no solo una pantalla), la página dice «Inicio» con el bloque «Algo salió
 * mal», su referencia y «Reintentar», sobre el fondo del tema, sin el marco que falló.
 */
export class FrameBoundary extends Component<{ children: ReactNode }, { reference: string | null }> {
  override state = { reference: null as string | null };

  static getDerivedStateFromError() {
    return { reference: newReference() };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    void reportClientError(error, info, this.state.reference ?? newReference());
  }

  override render() {
    return this.state.reference ? (
      <div className="tm adm-frame-error" data-part="marco-error">
        <main className="adm-frame-error-main">
          <UnexpectedError reference={this.state.reference} />
        </main>
      </div>
    ) : (
      this.props.children
    );
  }
}

async function reportClientError(error: Error, info: ErrorInfo, reference: string) {
  try {
    const { loadSession } = await import("../api");
    const session = await loadSession();
    await fetch("/telemetry/client-errors", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "X-CSRF-TOKEN": session.antiforgeryToken },
      body: JSON.stringify({
        errorKey: "BROWSER_RENDER_FAILED",
        message: `${reference}: ${error.message}`.slice(0, 500),
        stack: `${error.stack ?? ""}\n${info.componentStack ?? ""}`.slice(0, 4000),
        route: window.location.pathname,
        locale: document.documentElement.lang,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      }),
    });
  } catch {
    // El reporte nunca cambia lo que ve la persona.
  }
}
